import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Calendar, Clock, MapPin, Wallet, FileCheck, Plane, Luggage,
  HeartPulse, Coins, ClipboardCheck, CheckCircle2, Download, Sparkles,
  ArrowRight, Compass
} from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { NewsletterSignup } from '@/components/landing/NewsletterSignup';
import { useAuth } from '@/hooks/useAuth';

const ARTICLE_URL = 'https://odyvoy.com/blog/how-to-plan-a-trip';
const PUBLISH_DATE = '2026-06-24';
const READ_TIME = '12 min read';

const TOC_ITEMS = [
  { id: 'destination', label: 'Choose Your Destination', icon: MapPin },
  { id: 'budget', label: 'Set Your Travel Budget', icon: Wallet },
  { id: 'visa', label: 'Check Visa Requirements', icon: FileCheck },
  { id: 'book', label: 'Book Flights & Accommodation', icon: Plane },
  { id: 'itinerary', label: 'Build Your Itinerary', icon: Compass },
  { id: 'packing', label: 'Create a Smart Packing List', icon: Luggage },
  { id: 'health', label: 'Prepare Health & Safety', icon: HeartPulse },
  { id: 'money', label: 'Manage Money & Currency', icon: Coins },
  { id: 'checklist', label: 'Final Pre-Departure Checklist', icon: ClipboardCheck },
];

const HowToPlanTrip = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const title = 'The Ultimate Guide: How to Plan a Trip from Scratch';
  const description =
    'Learn how to plan a trip step by step. From choosing a destination and setting a budget to packing smart and checking visas — your complete travel planning checklist.';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`${title} | OdyVoy`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta name="keywords" content="how to plan a trip, trip planning guide, travel checklist, plan a vacation, travel planning steps, smart packing, visa check, travel budget" />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content="https://odyvoy.com/lovable-uploads/how-to-plan-a-trip-hero.png" />
        <meta property="article:published_time" content={PUBLISH_DATE} />
        <meta property="article:author" content="OdyVoy Travel Team" />
        <meta property="article:section" content="Travel Tips" />
        <meta property="article:tag" content="trip planning" />
        <meta property="article:tag" content="travel checklist" />
        <meta property="article:tag" content="budget travel" />
        <meta property="article:tag" content="packing guide" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://odyvoy.com/lovable-uploads/how-to-plan-a-trip-hero.png" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": title,
            "description": description,
            "image": "https://odyvoy.com/lovable-uploads/how-to-plan-a-trip-hero.png",
            "datePublished": PUBLISH_DATE,
            "dateModified": PUBLISH_DATE,
            "author": {
              "@type": "Organization",
              "name": "OdyVoy Travel Team"
            },
            "publisher": {
              "@type": "Organization",
              "name": "OdyVoy",
              "logo": {
                "@type": "ImageObject",
                "url": "https://odyvoy.com/lovable-uploads/odyvoy-logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": ARTICLE_URL
            },
            "keywords": "how to plan a trip, trip planning guide, travel checklist, smart packing, visa check, travel budget",
            "articleSection": "Travel Tips",
            "wordCount": 1800
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://odyvoy.com" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://odyvoy.com/blog" },
              { "@type": "ListItem", "position": 3, "name": title, "item": ARTICLE_URL }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 sm:h-16 items-center justify-between gap-2 px-3 sm:px-4">
          <button onClick={() => navigate(user ? '/dashboard' : '/')} className="shrink-0" aria-label="Go to homepage">
            <Logo className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16" />
          </button>
          <div className="flex items-center gap-1 sm:gap-3">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={() => navigate('/blog')} className="h-9 px-2 sm:px-3" aria-label="Back to blog">
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Back to Blog</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
              <Link to={user ? '/dashboard' : '/'} className="hover:text-foreground">Home</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-foreground">Blog</Link>
              <span>/</span>
              <span className="text-foreground">Travel Tips</span>
            </nav>

            <Badge variant="secondary" className="mb-4">Travel Tips</Badge>

            <h1 className="text-4xl font-bold mb-4 leading-tight">
              {title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                <span>OdyVoy Travel Team</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <time dateTime={PUBLISH_DATE}>
                  {new Date(PUBLISH_DATE).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </time>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{READ_TIME}</span>
              </div>
            </div>

            {/* Hero Image */}
            <img
              src="/lovable-uploads/how-to-plan-a-trip-hero.png"
              alt="Flat-lay of travel planning essentials including a world map, passport, smartphone, notebook, camera, and coffee on a dark blue surface"
              className="w-full h-[300px] sm:h-[400px] object-cover rounded-lg mb-8"
              width={1200}
              height={630}
              {...{ fetchpriority: "high" }}
            />

            {/* Intro */}
            <div className="prose prose-lg dark:prose-invert max-w-none mb-10">
              <p className="text-xl leading-relaxed text-muted-foreground">
                Planning a trip can feel overwhelming — but it does not have to be. Whether you are dreaming of a weekend city break or a month-long backpacking adventure, this step-by-step guide will walk you through everything you need to know about <strong>how to plan a trip</strong> from scratch. We have integrated OdyVoy's smartest tools at every stage so you can plan faster, pack lighter, and travel with confidence.
              </p>
            </div>

            {/* Table of Contents */}
            <Card className="mb-10 border border-border/40 bg-card/60 backdrop-blur-sm">
              <CardHeader>
                <h2 className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" />
                  What You Will Learn
                </h2>
              </CardHeader>
              <CardContent>
                <ol className="grid sm:grid-cols-2 gap-3">
                  {TOC_ITEMS.map((item, index) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className="flex items-center gap-2 text-sm text-left w-full hover:text-primary transition-colors group"
                      >
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          {index + 1}
                        </span>
                        <span className="group-hover:underline underline-offset-2">{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            {/* Step 1 */}
            <section id="destination" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">1</span>
                Choose Your Destination
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Every great trip starts with a decision: <em>where do you want to go?</em> The answer shapes your budget, packing list, visa needs, and itinerary. Start by asking yourself a few questions:</p>
                <ul>
                  <li>What kind of experience are you after — culture, nature, relaxation, or adventure?</li>
                  <li>What is your realistic budget for this trip?</li>
                  <li>How much time do you have?</li>
                  <li>What season works best for your schedule and the destination's weather?</li>
                </ul>
                <p>
                  Pro tip: Use OdyVoy's <Link to="/blog/where-to-travel-each-season-complete-guide" className="text-primary hover:underline">seasonal destination guide</Link> to match your travel dates with the best places to visit. If you are flexible, traveling during shoulder season (just before or after peak) can save you money and crowd headaches.
                </p>
              </div>
            </section>

            {/* Step 2 */}
            <section id="budget" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">2</span>
                Set Your Travel Budget
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Before you book anything, set a total budget and break it into categories:</p>
                <ul>
                  <li><strong>Transportation:</strong> Flights, trains, rental cars, local transit</li>
                  <li><strong>Accommodation:</strong> Hotels, hostels, vacation rentals</li>
                  <li><strong>Food &amp; drink:</strong> Restaurants, groceries, snacks</li>
                  <li><strong>Activities:</strong> Tours, museum tickets, adventure sports</li>
                  <li><strong>Miscellaneous:</strong> Visas, travel insurance, souvenirs, emergencies</li>
                </ul>
                <p>
                  A good rule of thumb: add 10–15% on top of your estimated total for unexpected expenses. OdyVoy's built-in budget tracker helps you monitor spending in real time and split group costs effortlessly — so you never come home to a scary credit card bill.
                </p>
                <p>
                  Looking for inspiration on saving money? Read our guide on <Link to="/blog/hacks-for-booking-cheaper-hotels-or-vacation-rentals" className="text-primary hover:underline">booking cheaper hotels and vacation rentals</Link>.
                </p>
              </div>
            </section>

            {/* Step 3 */}
            <section id="visa" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">3</span>
                Check Visa &amp; Entry Requirements
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Nothing derails a trip faster than showing up at the airport without the right paperwork. Visa rules change constantly, so always verify requirements directly before you travel.</p>
                <p>Here is what to confirm:</p>
                <ul>
                  <li>Do you need a visa, e-visa, or Electronic System for Travel Authorization (ESTA)?</li>
                  <li>How long does processing take?</li>
                  <li>Does your passport need to be valid for six months beyond your travel dates?</li>
                  <li>Are there any vaccination or health declaration requirements?</li>
                </ul>
                <p>
                  OdyVoy simplifies this with curated visa data for over 180 destinations. Instead of digging through embassy websites, you can check requirements in seconds and get direct links to official application portals. For example, see our <Link to="/visa/japan" className="text-primary hover:underline">Japan visa guide</Link> or <Link to="/visa/thailand" className="text-primary hover:underline">Thailand visa requirements</Link>.
                </p>
              </div>
            </section>

            {/* Step 4 */}
            <section id="book" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">4</span>
                Book Flights &amp; Accommodation
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Timing matters when booking flights. For international trips, aim to book 2–3 months in advance for the best prices. For domestic flights, 1–2 months is usually the sweet spot. Tuesdays and Wednesdays often have the lowest fares.</p>
                <p>When choosing accommodation, consider:</p>
                <ul>
                  <li><strong>Location:</strong> Is it close to public transit or the attractions you want to visit?</li>
                  <li><strong>Amenities:</strong> Do you need a kitchen, laundry, or workspace?</li>
                  <li><strong>Reviews:</strong> Look for consistent recent feedback on cleanliness and host responsiveness.</li>
                  <li><strong>Cancellation policy:</strong> Flexible cancellation is worth the slight premium for uncertain plans.</li>
                </ul>
                <p>
                  For more money-saving strategies, check out our guide on <Link to="/blog/how-to-find-the-best-flight-deals-in-2025-2026" className="text-primary hover:underline">finding the best flight deals</Link>.
                </p>
              </div>
            </section>

            {/* Step 5 */}
            <section id="itinerary" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">5</span>
                Build Your Itinerary
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>A well-balanced itinerary mixes must-see attractions with downtime. Over-scheduling leads to burnout; under-planning means missing out. Aim for 2–3 major activities per day, with buffer time for spontaneity.</p>
                <p>Practical itinerary tips:</p>
                <ul>
                  <li>Group activities by neighborhood to minimize transit time</li>
                  <li>Book popular attractions in advance (museums, food tours, hiking permits)</li>
                  <li>Leave one unplanned day for discoveries</li>
                  <li>Download offline maps and save key addresses before you leave</li>
                </ul>
                <p>
                  If you are visiting multiple cities, OdyVoy's multi-city planner (available for First Class subscribers) lets you organize complex itineraries with packing and visa considerations built in for each stop.
                </p>
              </div>
            </section>

            {/* Step 6 */}
            <section id="packing" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">6</span>
                Create a Smart Packing List
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Packing is where most travelers either overpack or forget essentials. The key is to pack for your <em>actual</em> itinerary and destination weather — not a hypothetical version of your trip.</p>
                <p>OdyVoy's AI-powered packing assistant takes the guesswork out by generating a personalized list based on:</p>
                <ul>
                  <li>Destination climate and seasonal weather patterns</li>
                  <li>Trip duration and type (business, leisure, adventure)</li>
                  <li>Your luggage weight allowance</li>
                  <li>Local customs and dress codes</li>
                </ul>
                <p>
                  The app even auto-optimizes your list if you are overweight — suggesting which non-essential items to move to checked baggage or leave behind. No more sitting on your suitcase at 2 AM.
                </p>
                <p>
                  Explore our destination-specific packing guides like <Link to="/packing-list/japan" className="text-primary hover:underline">Japan packing list</Link> and <Link to="/packing-list/greece" className="text-primary hover:underline">Greece packing list</Link>, or read <Link to="/blog/how-to-pack-light-without-leaving-the-essentials-behind" className="text-primary hover:underline">how to pack light without leaving essentials behind</Link>.
                </p>
              </div>
            </section>

            {/* Step 7 */}
            <section id="health" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">7</span>
                Prepare Health &amp; Safety
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Health preparation is not glamorous, but it is essential. At least 4–6 weeks before departure:</p>
                <ul>
                  <li>Check if any vaccinations are recommended or required</li>
                  <li>Refill prescription medications and pack them in your carry-on</li>
                  <li>Buy travel insurance that covers medical emergencies and trip cancellation</li>
                  <li>Research local emergency numbers and nearest hospitals</li>
                </ul>
                <p>
                  If you are crossing multiple time zones, prepare a jet lag strategy. Our guide on <Link to="/blog/how-to-beat-jet-lag-science-backed-strategies" className="text-primary hover:underline">beating jet lag with science-backed strategies</Link> covers light exposure, melatonin timing, and meal planning to help you adjust faster.
                </p>
              </div>
            </section>

            {/* Step 8 */}
            <section id="money" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">8</span>
                Manage Money &amp; Currency
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Financial prep prevents stress abroad. Here is a quick checklist:</p>
                <ul>
                  <li>Notify your bank of travel dates to avoid card blocks</li>
                  <li>Carry a small amount of local currency for arrival</li>
                  <li>Have a backup card stored separately from your main one</li>
                  <li>Understand ATM fees and foreign transaction charges</li>
                </ul>
                <p>
                  OdyVoy's currency converter lets you snap a photo of a price tag and instantly see the conversion — perfect for markets, restaurants, and quick purchases. The expense splitter also makes group trips painless by tracking who paid for what.
                </p>
              </div>
            </section>

            {/* Step 9 */}
            <section id="checklist" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">9</span>
                Final Pre-Departure Checklist
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>The week before you leave, run through this final checklist:</p>
                <ul>
                  <li>Check in for your flight and download your boarding pass</li>
                  <li>Confirm accommodation check-in instructions</li>
                  <li>Share your itinerary with a trusted friend or family member</li>
                  <li>Charge all devices and pack universal adapters</li>
                  <li>Take photos of important documents (passport, visa, insurance)</li>
                  <li>Set up an eSIM or confirm your roaming plan</li>
                  <li>Water your plants, hold your mail, and lock up valuables</li>
                </ul>
                <p>
                  Once you are at the airport, relax. The hardest part — planning — is done. Now the adventure begins.
                </p>
              </div>
            </section>

            {/* CTA Card */}
            <Card className="mb-10 border-primary/20 bg-primary/5">
              <CardContent className="p-6 sm:p-8 text-center">
                <h3 className="text-xl sm:text-2xl font-bold mb-3">
                  Ready to Plan Your Next Trip?
                </h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  OdyVoy turns this checklist into an automated, personalized travel companion. Get smart packing lists, visa checks, budget tracking, and more — all in one app.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button size="lg" onClick={() => navigate('/auth')} className="min-h-[48px] px-6">
                    <Download className="mr-2 h-5 w-5" />
                    Get Started Free
                  </Button>
                  <Button variant="outline" size="lg" onClick={() => navigate('/pricing')} className="min-h-[48px] px-6">
                    View Plans <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tags */}
            <div className="mt-8 pt-8 border-t">
              <div className="flex flex-wrap gap-2">
                {['trip planning', 'travel checklist', 'budget travel', 'smart packing', 'visa guide', 'travel tips'].map((tag) => (
                  <Badge key={tag} variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors" onClick={() => navigate('/blog')}>
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Newsletter */}
            <div className="mt-12">
              <NewsletterSignup />
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              {/* Quick Nav */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ClipboardCheck className="h-5 w-5 text-primary" />
                    In This Guide
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {TOC_ITEMS.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="flex items-center gap-2 text-sm w-full text-left hover:text-primary transition-colors group"
                    >
                      <item.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                      <span className="group-hover:underline underline-offset-2">{item.label}</span>
                    </button>
                  ))}
                </CardContent>
              </Card>

              {/* Popular Destinations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MapPin className="h-5 w-5 text-primary" />
                    Popular Destinations
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { name: 'Japan', slug: 'japan' },
                    { name: 'Greece', slug: 'greece' },
                    { name: 'Italy', slug: 'italy' },
                    { name: 'Thailand', slug: 'thailand' },
                  ].map((dest) => (
                    <div key={dest.slug} className="flex items-center justify-between">
                      <button
                        onClick={() => navigate(`/packing-list/${dest.slug}`)}
                        className="text-sm font-medium hover:text-primary transition-colors"
                      >
                        {dest.name} Packing List
                      </button>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Feature Highlight */}
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-5">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Try OdyVoy Free
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Get AI-powered packing lists, visa checks, and budget tools for your next adventure.
                  </p>
                  <Button className="w-full" size="sm" onClick={() => navigate('/auth')}>
                    Start Planning
                  </Button>
                </CardContent>
              </Card>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default HowToPlanTrip;
